import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to track time spent on a page, persisting across browser sessions
 *
 * @param {Object} options - Configuration options
 * @param {string} options.key - localStorage key for persisting time data
 * @param {number} options.threshold - Primary time threshold in seconds to trigger callback
 * @param {number} options.secondaryThreshold - Secondary time threshold in seconds to trigger callback again
 * @param {number} options.tertiaryThreshold - Tertiary time threshold in seconds to trigger callback again
 * @param {number} options.saveInterval - Interval in ms to save to localStorage
 * @param {number} options.tickInterval - Interval in ms to update timer
 * @param {boolean} options.trackWhenHidden - Whether to track time when tab is not visible
 * @param {boolean} options.isLabLoaded - Flag indicating if the main lab component has loaded
 * @param {Function} options.onThresholdReached - Callback when primary threshold is reached
 * @param {Function} options.onSecondaryThresholdReached - Callback when secondary threshold is reached
 * @param {Function} options.onTertiaryThresholdReached - Callback when tertiary threshold is reached
 * @returns {Object} Timer state and control functions
 */
function useTimeTracker({
  key = 'page_time_tracker',
  threshold = 300, // 5 minutes default
  secondaryThreshold = 1800, // 30 minutes default
  tertiaryThreshold = 3600, // 60 minutes default
  saveInterval = 5000, // Save every 5 seconds
  tickInterval = 1000, // Update every second
  trackWhenHidden = false,
  isLabLoaded = false, // Add new parameter with default
  onThresholdReached = () => {},
  onSecondaryThresholdReached = () => {},
  onTertiaryThresholdReached = () => {}
} = {}) {
  // Use refs for values that shouldn't trigger re-renders when they change
  const timeRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastSaveTimeRef = useRef(0);
  const thresholdReachedRef = useRef(false);
  const secondaryThresholdReachedRef = useRef(false);
  const tertiaryThresholdReachedRef = useRef(false);
  const isActiveRef = useRef(true);
  const isVisibleRef = useRef(true);
  const timerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const sentMilestonesRef = useRef(new Set()); // Ref to track sent GA events

  // State that needs to trigger re-renders
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [thresholdReached, setThresholdReached] = useState(false);
  const [secondaryThresholdReached, setSecondaryThresholdReached] = useState(false);
  const [tertiaryThresholdReached, setTertiaryThresholdReached] = useState(false);

  // Define time milestones in seconds (e.g., 1, 5, 10, 15, 30, 60 minutes)
  const milestonesInSeconds = useRef([
      60,       // 1 min
      300,      // 5 min
      600,      // 10 min
      900,      // 15 min
      1800,     // 30 min
      3600,     // 60 min (1 hour)
      7200,     // 2 hours
      10800     // 3 hours
      // Add more milestones as needed
  ]).current;

  // Load saved time from localStorage on mount only
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        const { time, primaryReached, secondaryReached, tertiaryReached } = JSON.parse(savedData);
        timeRef.current = time;
        setElapsedTime(time);
        
        // Restore threshold states from localStorage
        if (primaryReached) {
          thresholdReachedRef.current = true;
          setThresholdReached(true);
        }
        
        if (secondaryReached) {
          secondaryThresholdReachedRef.current = true;
          setSecondaryThresholdReached(true);
        }
        
        if (tertiaryReached) {
          tertiaryThresholdReachedRef.current = true;
          setTertiaryThresholdReached(true);
        }
        
        // Check if we need to trigger callbacks based on current time
        if (time >= threshold && !thresholdReachedRef.current) {
          thresholdReachedRef.current = true;
          setThresholdReached(true);
          onThresholdReached(time);
        }
        
        if (time >= secondaryThreshold && !secondaryThresholdReachedRef.current) {
          secondaryThresholdReachedRef.current = true;
          setSecondaryThresholdReached(true);
          onSecondaryThresholdReached(time);
        }
        
        if (time >= tertiaryThreshold && !tertiaryThresholdReachedRef.current) {
          tertiaryThresholdReachedRef.current = true;
          setTertiaryThresholdReached(true);
          onTertiaryThresholdReached(time);
        }
      }
    } catch (error) {
      console.error('Error loading time data from localStorage:', error);
    }
    
    // Set up visibility change listener
    const handleVisibilityChange = () => {
      const isDocVisible = document.visibilityState === 'visible';
      isVisibleRef.current = isDocVisible;
      setIsVisible(isDocVisible);
      
      // If becoming hidden, save current time
      if (!isDocVisible) {
        saveTimeToStorage();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Set up timer
    startTimer();
    
    // Set up periodic save
    saveTimerRef.current = setInterval(() => {
      if (Date.now() - lastSaveTimeRef.current >= saveInterval) {
        saveTimeToStorage();
      }
    }, saveInterval);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopTimer();
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
      saveTimeToStorage(); // Final save on unmount
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount/unmount
  
  // Save time to localStorage
  const saveTimeToStorage = useCallback(() => {
    if (timeRef.current > 0) {
      try {
        localStorage.setItem(key, JSON.stringify({
          time: timeRef.current,
          lastUpdated: Date.now(),
          primaryReached: thresholdReachedRef.current,
          secondaryReached: secondaryThresholdReachedRef.current,
          tertiaryReached: tertiaryThresholdReachedRef.current
        }));
        lastSaveTimeRef.current = Date.now();
      } catch (error) {
        console.error('Error saving time data to localStorage:', error);
      }
    }
  }, [key]);
  
  // Start the timer
  const startTimer = useCallback(() => {
    if (timerRef.current) return; // Already running
    
    startTimeRef.current = Date.now();
    
    timerRef.current = setInterval(() => {
      if (isActiveRef.current && (isVisibleRef.current || trackWhenHidden)) {
        // Update time
        timeRef.current += 1;
        setElapsedTime(timeRef.current);
        
        // Check primary threshold
        if (timeRef.current >= threshold && !thresholdReachedRef.current) {
          thresholdReachedRef.current = true;
          setThresholdReached(true);
          onThresholdReached(timeRef.current);
        }
        
        // Check secondary threshold
        if (timeRef.current >= secondaryThreshold && !secondaryThresholdReachedRef.current) {
          secondaryThresholdReachedRef.current = true;
          setSecondaryThresholdReached(true);
          onSecondaryThresholdReached(timeRef.current);
        }
        
        // Check tertiary threshold
        if (timeRef.current >= tertiaryThreshold && !tertiaryThresholdReachedRef.current) {
          tertiaryThresholdReachedRef.current = true;
          setTertiaryThresholdReached(true);
          onTertiaryThresholdReached(timeRef.current);
        }

        // --- Check for GA Milestones ---
        if (isLabLoaded) { // Only check/send if lab is loaded
            milestonesInSeconds.forEach(milestoneSecs => {
                // Check if the current time just crossed the milestone AND it hasn't been sent yet
                if (timeRef.current >= milestoneSecs && !sentMilestonesRef.current.has(milestoneSecs)) {
                    const minutes = milestoneSecs / 60;
                    const eventName = `time_elapsed_${minutes}_min`;

                    // Send GA event
                    if (typeof gtag === 'function') {
                        gtag('event', eventName, {
                            'event_category': 'Timing',
                            'event_label': `${minutes} minute${minutes > 1 ? 's' : ''} reached`,
                            'value': milestoneSecs // Send time in seconds
                        });
                    } else {
                        //console.warn('gtag function not found. Cannot send GA event:', eventName);
                    }

                    // Mark this milestone as sent for this session
                    sentMilestonesRef.current.add(milestoneSecs);
                }
            });
        }
        // --- End GA Milestone Check ---

      }
    }, tickInterval);
  }, [threshold, secondaryThreshold, tertiaryThreshold, tickInterval, trackWhenHidden, onThresholdReached, onSecondaryThresholdReached, onTertiaryThresholdReached, isLabLoaded, milestonesInSeconds]); // Add isLabLoaded and milestonesInSeconds

  // Stop the timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  
  // Format seconds to HH:MM:SS
  const formatTime = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      secs.toString().padStart(2, '0')
    ].join(':');
  }, []);
  
  // Reset timer
  const resetTimer = useCallback(() => {
    timeRef.current = 0;
    setElapsedTime(0);
    thresholdReachedRef.current = false;
    setThresholdReached(false);
    secondaryThresholdReachedRef.current = false;
    setSecondaryThresholdReached(false);
    sentMilestonesRef.current.clear(); // Clear sent milestones on reset

    try {
      localStorage.setItem(key, JSON.stringify({
        time: 0,
        lastUpdated: Date.now(),
        primaryReached: false,
        secondaryReached: false,
        tertiaryReached: false
      }));
    } catch (error) {
      console.error('Error resetting time data in localStorage:', error);
    }
  }, [key]);
  
  // Pause timer
  const pauseTimer = useCallback(() => {
    isActiveRef.current = false;
    setIsActive(false);
    saveTimeToStorage();
  }, [saveTimeToStorage]);
  
  // Resume timer
  const resumeTimer = useCallback(() => {
    isActiveRef.current = true;
    setIsActive(true);
    startTimeRef.current = Date.now();
  }, []);
  
  return {
    elapsedTime,
    formattedTime: formatTime(elapsedTime),
    isActive,
    isVisible,
    thresholdReached,
    secondaryThresholdReached,
    tertiaryThresholdReached,
    resetTimer,
    pauseTimer,
    resumeTimer,
    // Add a method to reset just the primary threshold
    resetPrimaryThreshold: useCallback(() => {
      thresholdReachedRef.current = false;
      setThresholdReached(false);
      saveTimeToStorage();
    }, [saveTimeToStorage]),
    // Add a method to reset just the secondary threshold
    resetSecondaryThreshold: useCallback(() => {
      secondaryThresholdReachedRef.current = false;
      setSecondaryThresholdReached(false);
      saveTimeToStorage();
    }, [saveTimeToStorage]),
    // Add a method to reset just the tertiary threshold
    resetTertiaryThreshold: useCallback(() => {
      tertiaryThresholdReachedRef.current = false;
      setTertiaryThresholdReached(false);
      saveTimeToStorage();
    }, [saveTimeToStorage])
  };
}

export default useTimeTracker;
